import { type TFunction } from 'i18next';
import * as yup from 'yup';

import {
  IdValidationConfigPreset,
  OrganizationDTOStatus,
  OrganizationUpdateRequestDTOEmployeeCount,
  OrganizationUpdateRequestDTOTalentUniqueFieldsItem,
  PlatformLocale
} from '@/api/generated';
import { PROCESS_SETTINGS_STEP_KEYS } from '@/root/linker';
import {
  getProcessSettingsAccessInvitationValidation,
  getProcessSettingsTestPropertiesValidation
} from '@/views/ProcessSettingsForm/schema';

import { MFAConfigKeys } from './authOptions';
import { ID_STRUCTURE_OPTIONS, type OrganizationSettingsFieldValues } from './types';

export const getBasicInfoValidationSchema = (t: TFunction) =>
  yup
    .object()
    .transform(currentValue => ({
      ...currentValue,
      anonymizationDurationMonths: currentValue?.anonymizationDurationMonths || 0
    }))
    .shape({
      name: yup
        .string()
        .min(
          1,
          t('validation:limits.minimum', {
            field: t('orgSettings.fields.organizationName'),
            count: 1,
            unit: t('validation:unit.character')
          })
        )
        .max(
          25,
          t('validation:limits.maximum', {
            field: t('orgSettings.fields.organizationName'),
            count: 25,
            unit: t('validation:unit.characters')
          })
        )
        .required(t('validation:mandatory', { field: t('orgSettings.fields.organizationName') })),

      legalName: yup
        .string()
        .min(
          1,
          t('validation:limits.minimum', {
            field: t('orgSettings.fields.legalName'),
            count: 1,
            unit: t('validation:unit.character')
          })
        )
        .max(
          50,
          t('validation:limits.maximum', {
            field: t('orgSettings.fields.legalName'),
            count: 50,
            unit: t('validation:unit.characters')
          })
        )
        .required(t('validation:mandatory', { field: t('orgSettings.fields.legalName') })),

      offices: yup
        .array()
        .of(yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.office') })))
        .min(1, t('validation:atLeastOne', { field: t('orgSettings.fields.office') }))
        .required(t('validation:mandatory', { field: t('orgSettings.fields.office') })),

      countries: yup
        .array()
        .of(yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.orgCountry') })))
        .min(1, t('validation:atLeastOne', { field: t('orgSettings.fields.orgCountry') }))
        .required(t('validation:mandatory', { field: t('orgSettings.fields.orgCountry') })),

      industry: yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.industry') })),

      employeeCount: yup
        .string()
        .oneOf(
          Object.keys(OrganizationUpdateRequestDTOEmployeeCount),
          t('validation:validSelection', { field: t('orgSettings.fields.numberOfEmployees') })
        )
        .required(t('validation:mandatory', { field: t('orgSettings.fields.numberOfEmployees') })),

      jiraId: yup.string().nullable(),
      pipedriveId: yup.string().nullable(),
      legacyId: yup.string().nullable(),

      status: yup
        .string()
        .oneOf(
          Object.keys(OrganizationDTOStatus),
          t('validation:validSelection', { field: t('orgSettings.fields.status') })
        )
        .required(t('validation:mandatory', { field: t('orgSettings.fields.status') }))
    }) as yup.Schema<OrganizationSettingsFieldValues>;

export const getRolesMembersValidationSchema = (t: TFunction) =>
  yup.object().shape({
    admins: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string(),
          email: yup.string().email(),
          phone: yup.string()
        })
      )
      .min(1, t('validation:atLeastOne', { field: t('orgSettings.fields.organizationAdmins') })),

    userInviteEmailLanguage: yup
      .string()
      .oneOf(
        Object.keys(PlatformLocale),
        t('validation:validSelection', { field: t('orgSettings.fields.userInviteEmailLanguage') })
      ),

    authConfig: yup.object().shape({
      invalidateAfterDays: yup.number().nullable(),
      methods: yup
        .array()
        .of(
          yup
            .string()
            .oneOf(
              Object.values(MFAConfigKeys),
              t('validation:validSelection', { field: t('orgSettings.fields.mfaMethods') })
            )
        )
    })
  }) as yup.Schema<OrganizationSettingsFieldValues>;

export const getTestsProcessesValidationSchema = (t: TFunction) =>
  yup.object().shape({
    [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: getProcessSettingsAccessInvitationValidation(t),
    [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: getProcessSettingsTestPropertiesValidation(t)
  }) as yup.Schema<OrganizationSettingsFieldValues>;

export const getTalentRegistrationValidationSchema = (t: TFunction) =>
  yup.object().shape({
    testValidityDays: yup
      .number()
      .min(
        0,
        t('validation:limits.minimum', {
          field: t('orgSettings.fields.accessValidFor'),
          count: 0,
          unit: t('validation:unit.days')
        })
      )
      .max(
        365 * 10,
        t('validation:limits.maximum', {
          field: t('orgSettings.fields.accessValidFor'),
          count: 365 * 10,
          unit: t('validation:unit.days')
        })
      )
      .nullable(),

    talentUniqueFields: yup
      .string()
      .oneOf(
        Object.values(OrganizationUpdateRequestDTOTalentUniqueFieldsItem),
        t('validation:validSelection', { field: t('orgSettings.fields.idFields') })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.idFields') })),

    idValidation: yup.object().shape({
      preset: yup
        .string()
        .when('talentUniqueFields', {
          is: (idFieldsValue: Values<typeof OrganizationUpdateRequestDTOTalentUniqueFieldsItem>[]) =>
            Array.isArray(idFieldsValue) &&
            idFieldsValue.includes(OrganizationUpdateRequestDTOTalentUniqueFieldsItem.ID_NUMBER),

          then: validation =>
            validation
              .oneOf(
                Object.values(IdValidationConfigPreset),
                t('validation:validSelection', { field: t('orgSettings.fields.validationTypes') })
              )
              .required(t('validation:mandatory', { field: t('orgSettings.fields.validationTypes') })),

          otherwise: validation => validation
        })
        .nullable(),

      structure: yup.string().when(['talentUniqueFields', 'idValidation.preset'], {
        is: (
          idFieldsValue: Values<typeof OrganizationUpdateRequestDTOTalentUniqueFieldsItem>[],
          preset: IdValidationConfigPreset
        ) =>
          Array.isArray(idFieldsValue) &&
          idFieldsValue.includes(OrganizationUpdateRequestDTOTalentUniqueFieldsItem.ID_NUMBER) &&
          preset === IdValidationConfigPreset.CUSTOM,

        then: validation =>
          validation
            .oneOf(
              Object.values(ID_STRUCTURE_OPTIONS),
              t('validation:validSelection', { field: t('orgSettings.fields.idStructure') })
            )
            .required(t('validation:mandatory', { field: t('orgSettings.fields.idStructure') })),

        otherwise: validation => validation
      }),

      maxLength: yup.number().when(['idValidation.structure', 'talentUniqueFields', 'idValidation.preset'], {
        is: (
          idStructureValue: Values<typeof ID_STRUCTURE_OPTIONS>,
          idFieldsValue: Values<typeof OrganizationUpdateRequestDTOTalentUniqueFieldsItem>[],
          preset: IdValidationConfigPreset
        ) =>
          idStructureValue === ID_STRUCTURE_OPTIONS.BOTH &&
          Array.isArray(idFieldsValue) &&
          idFieldsValue.includes(OrganizationUpdateRequestDTOTalentUniqueFieldsItem.ID_NUMBER) &&
          preset === IdValidationConfigPreset.CUSTOM,

        then: validation =>
          validation
            .min(1, t('validation:mandatory', { field: t('orgSettings.fields.maxChars') }))
            .required(t('validation:mandatory', { field: t('orgSettings.fields.maxChars') })),

        otherwise: validation => validation
      })
    })
  }) as yup.Schema<OrganizationSettingsFieldValues>;
